import {GitHubAvatar} from '@github-ui/github-avatar'
import {useQueryClient} from '@github-ui/react-query'
import type {EditHistoryDialogQueryQuery} from '@github-ui/server-persisted-queries/types/EditHistoryDialogQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TrashIcon} from '@primer/octicons-react'
import {Button, Label, Link, RelativeTime, useConfirm} from '@primer/react'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {BUTTON_LABELS} from '../constants/button-labels'
import {ERRORS} from '../constants/errors'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import {editHistoryDialogQueryKey} from '../fetch-edit-history-dialog'
import {commitDeleteUserContentEditMutation} from '../mutations/delete-user-content-edit-mutation'
import styles from './EditHistoryDialogHeader.module.css'

// Narrow the `node` union to the resolved UserContentEdit variant (identified by
// its `editedAt` scalar; the empty-object variant has no fields).
type EditHistoryNode = Extract<NonNullable<EditHistoryDialogQueryQuery['node']>, {editedAt: string}>
export type EditHistoryDialogHeaderFragment = Pick<
  EditHistoryNode,
  'editor' | 'deletedAt' | 'editedAt' | 'newest' | 'firstEdit' | 'viewerCanDelete'
>

export const EditHistoryDialogHeader = ({
  data,
  userContentEditId,
}: {
  data: EditHistoryDialogHeaderFragment
  userContentEditId: string
}) => {
  const environment = useRelayEnvironment()
  const confirm = useConfirm()
  const {addToast} = useToastContext()
  const queryClient = useQueryClient()
  const deleteRevision = useCallback(async () => {
    if (
      await confirm({
        title: LABELS.confirmations.deleteEditHistoryTitle,
        content: LABELS.confirmations.deleteEditHistoryContent,
        confirmButtonType: 'danger',
        confirmButtonContent: LABELS.confirmations.deleteEditHistoryConfirmButtonContent,
      })
    ) {
      commitDeleteUserContentEditMutation({
        environment,
        input: {id: userContentEditId},
        onCompleted: () => {
          // The dialog reads its data from TanStack Query, so invalidate the query to
          // refresh the open dialog into its deleted state (the Relay store update from
          // the mutation no longer propagates to the dialog on its own).
          void queryClient.invalidateQueries({queryKey: editHistoryDialogQueryKey(userContentEditId)})
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotDeleteEditRevision,
          })
        },
      })
    }
  }, [addToast, confirm, environment, queryClient, userContentEditId])

  const hasTrailingElement = data.newest || data.deletedAt

  return (
    <div className={styles.EditHistoryDialogHeaderContainer}>
      {!!data.editor && (
        <ActorOperationDetails
          login={data.editor.login}
          avatarUrl={data.editor.avatarUrl}
          isBot={data.editor.__typename === 'Bot'}
          time={data.editedAt}
          details={data.firstEdit ? LABELS.editHistory.created : LABELS.editHistory.edited}
          trailingElement={
            hasTrailingElement ? (
              <Label className={styles.statusLabel}>
                {data.deletedAt ? LABELS.editHistory.deleted : LABELS.editHistory.mostRecent}
              </Label>
            ) : undefined
          }
        />
      )}
      {!!data.viewerCanDelete && !data.newest && !data.deletedAt && (
        <Button
          variant="danger"
          size="small"
          aria-label={LABELS.editHistory.deleteRevisionAriaLabel}
          onClick={deleteRevision}
          className={styles.deleteRevisionButton}
        >
          <TrashIcon /> {BUTTON_LABELS.deleteEditRevision}
        </Button>
      )}
    </div>
  )
}

type ActorOperationDetailsProps = {
  login?: string
  avatarUrl?: string
  details: string
  time?: string
  isBot?: boolean
  trailingElement?: React.ReactElement
  className?: string
}

export function ActorOperationDetails({
  login,
  avatarUrl,
  details,
  time,
  isBot = false,
  className,
  trailingElement,
}: ActorOperationDetailsProps) {
  const showUserAvatar = !!avatarUrl && !!login
  const shouldLinkToProfile = !!login && !isBot
  return (
    <div className={styles.actorDetailsWrapper}>
      <div className={className}>
        {showUserAvatar && (
          <>
            <GitHubAvatar src={avatarUrl} size={16} alt={`@${login}`} className={styles.userAvatar} />
            {shouldLinkToProfile ? (
              <Link href={`/${login}`} className={styles.usernameBold} aria-label={`View profile for @${login}`}>
                {login}
              </Link>
            ) : (
              <span className={styles.usernameBold}>{login}</span>
            )}
          </>
        )}
        {!showUserAvatar && <span>{login ?? VALUES.ghostUserLogin}</span>}
        <span> {details} </span>
        {time && <RelativeTime date={new Date(time)} />}
      </div>
      {trailingElement}
    </div>
  )
}
